import type { IResource } from "@gettactic/api";
import { dedicatedSeatCount } from "@gettactic/helpers/src/resources/dedicatedSeats";

/** What one release frees: the whole desk, or one owner's seat of a shared workspace. */
export type ReleaseNoun = "desk" | "seat";

export const releaseNoun = (
  resource: Pick<IResource, "type" | "total_slots">
): ReleaseNoun => (dedicatedSeatCount(resource) > 1 ? "seat" : "desk");

/**
 * What the API said when a release could not be made, in the words the
 * builder shows. Codes come from the resource dedicated exceptions endpoints.
 */
export const releaseErrorMessage = (
  code: string | undefined,
  noun: ReleaseNoun = "desk",
  ownerName = "This person"
): string => {
  switch (code) {
    case "dedicated_exception_overlaps":
      return `These days overlap another release of this ${noun}.`;
    case "invalid_time_range":
      return "The last day must not be before the first, nor before today.";
    case "grantees_required":
      return `Pick at least one team or person to release the ${noun} to.`;
    case "grantee_not_found":
      return "One of the teams or people no longer exists.";
    case "forbidden":
      return `Only the ${noun}'s owner or a resource manager can release it.`;
    case "dedicated_user_id_required":
      return "Choose whose seat to release.";
    case "not_a_dedicated_user":
      return `${ownerName} no longer owns a seat here. Refresh and try again.`;
    default:
      return `Could not release the ${noun}. Try again.`;
  }
};

export interface DedicatedExceptionsText {
  releaseLabel: string;
  intro: string;
  checking: string;
  checkFailed: string;
  tooMany: string;
  unreachable: string;
  releasedToast: (cancelled: number) => string;
  /** `from` and `to` are days already formatted for display. */
  releaseSentence: (from: string, to: string, isOpen: boolean) => string;
  withdrawSentence: (from: string, to: string) => string;
}

/**
 * The release section's wording. A single seat keeps the desk wording it
 * always had; one owner's seat of a shared workspace is called a seat.
 */
export const dedicatedExceptionsText = (
  noun: ReleaseNoun,
  ownerName: string
): DedicatedExceptionsText => {
  const capitalNoun = noun === "seat" ? "Seat" : "Desk";
  return {
    releaseLabel: `Release ${noun}`,
    intro: `While ${ownerName} is away the ${noun} can be released for some days: to anyone, or to the teams and people you name. Those days it is booked like any other ${noun}.`,
    checking: `Checking the ${noun}'s reservations…`,
    checkFailed: `Could not check the ${noun}'s reservations.`,
    tooMany: `The ${noun} has too many reservations to list.`,
    unreachable: `Couldn't reach the server. The ${noun} was not released.`,
    releasedToast: (cancelled) =>
      cancelled > 0
        ? `${capitalNoun} released. ${cancelled} reservation${cancelled === 1 ? "" : "s"} cancelled.`
        : `${capitalNoun} released.`,
    releaseSentence: (from, to, isOpen) =>
      `From ${from} to ${to} the ${noun} can be booked by ${
        isOpen ? "anyone" : "the teams and people you named"
      }.`,
    withdrawSentence: (from, to) =>
      noun === "seat"
        ? `From ${from} to ${to} the seat goes back to ${ownerName}.`
        : `From ${from} to ${to} the desk goes back to ${ownerName} alone.`
  };
};
